import { Hymn, OrgType, SongListEntry, SongListEntryType } from '../../../../../common/src/model.ts';
import { splitVersesSuffix } from '../../../../../common/src/verses.ts';
import { HymnalWithHymns } from '../../../data/hymnals.ts';
import { resolveExactDisplayName, resolveSearchStringToSongSlug } from '../../../util/path.ts';

/**
 * The entry a planner cell gets from pasted text: a song row binds the text to a song when it names one,
 * carrying the verses of a suffix such as '517 - All Hail the Power of Jesus' Name!  vv. 1–3, 6' (issue #4169);
 * anything else keeps the text as it was pasted. Undefined for empty text.
 *
 * For a church, in order: a song whose name is exactly the text (a title may itself end in what reads like a suffix,
 * 'Amazing Grace v. 2'), then with its suffix split off, a song whose name is exactly the rest, then what a paste did
 * before verses existed: any song the search for the whole text finds, else the text. A school (any org other than a
 * church) pastes as before #4212, with one more step after the exact name of the rest: any song the search for the rest finds, with the verses; then
 * the search for the whole text, else the text.
 *
 * Exact names come before any search because the search matches on song numbers and parts of titles: with custom songs
 * 'Amazing Grace (My Chains Are Gone)' and 'Amazing Grace', 'Amazing Grace' is the second. A church binds the rest
 * before a suffix by exact name only (issue #4212): the search would bind 'Psalm 23 vv. 1–6' copied from a Scripture
 * row to a hymnal song titled 'Psalm 23' with verses 1 to 6, where the text belongs, and a number before a suffix
 * ('517 vv. 1–3') is no song's name either, so it stays text. An org not yet loaded takes the church's rule, the
 * stricter one. For both the search for the whole text is the last step, as the loosest match: a number binds only a
 * song numbered exactly the whole text and a title only one containing all of it, suffix included, so it binds text
 * with no suffix ('517', 'Psalm 23'); ahead of the split it would bind '517 - All Hail…  vv. 1–3, 6' to song 517 and
 * lose the verses.
 */
export function entryFromPastedText({
  row,
  text,
  entryType,
  hymnals,
  customHymns,
  isInternalUser,
  orgType,
}: {
  row: number;
  text: string;
  entryType: SongListEntryType;
  hymnals: HymnalWithHymns[] | undefined;
  customHymns: Hymn[] | undefined;
  isInternalUser: boolean;
  // the planner's org's type, undefined before it loads: only an org known not to be a church searches for the song
  // before a suffix
  orgType: OrgType | undefined;
}): SongListEntry | undefined {
  if (!text) {
    return;
  }
  if (entryType !== SongListEntryType.Song) {
    return { row, text };
  }
  const exactSlug = resolveExactDisplayName(hymnals, customHymns, text, isInternalUser);
  if (exactSlug) {
    return { row, slug: exactSlug };
  }
  const { text: name, verses } = splitVersesSuffix(text);
  const searchesBeforeSuffix = orgType !== undefined && orgType !== OrgType.Church;
  const slugWithoutSuffix =
    verses &&
    (resolveExactDisplayName(hymnals, customHymns, name, isInternalUser) ??
      (searchesBeforeSuffix ? resolveSearchStringToSongSlug(hymnals, customHymns, name, isInternalUser) : undefined));
  if (slugWithoutSuffix) {
    return { row, slug: slugWithoutSuffix, verses };
  }
  const slug = resolveSearchStringToSongSlug(hymnals, customHymns, text, isInternalUser);
  return slug ? { row, slug } : { row, text };
}
